import { cn } from "@newxsystems/shared";
import { resolveKaajPrimitive } from "../../../../client";
import type { BreadcrumbBlockVariant, IBreadcrumbBlockProps } from "./BreadcrumbBlock.props";

const BreadcrumbSeparator = ({ variant }: { variant: BreadcrumbBlockVariant }) => (
  <span
    aria-hidden="true"
    className={cn(
      "inline-flex shrink-0 items-center justify-center text-[#9AAEFF]",
      variant === "deep" ? "mx-0.5 size-5" : "mx-0.5 size-4",
    )}
  >
    <svg className={cn("size-3.5", variant === "deep" && "size-4")} fill="none" viewBox="0 0 16 16">
      <path
        d="m6 4 4 4-4 4"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  </span>
);

export const BreadcrumbBlock = ({ items, variant }: IBreadcrumbBlockProps) => {
  const Link = resolveKaajPrimitive("Link");
  const isDeep = variant === "deep";

  if (!items?.length) {
    return null;
  }

  const breadcrumbItems = (
    <ol
      className={cn(
        "flex min-w-0 items-center",
        isDeep ? "flex-wrap gap-x-1 gap-y-2 sm:gap-x-1.5" : "min-w-max gap-1.5",
      )}
    >
      {items.map((item, index) => {
        const isCurrent = Boolean(item.current);
        const isDisabled = Boolean(item.disabled || !item.href);
        const label = item.label ?? item.text;
        const contentClassName = cn(
          "inline-flex min-h-11 items-center rounded-full px-3.5 py-2 text-sm font-extrabold leading-5 tracking-[-0.01em] transition-[background-color,border-color,box-shadow,color,transform] duration-200 [overflow-wrap:anywhere] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2144FF] focus-visible:ring-offset-2 focus-visible:ring-offset-white motion-reduce:transition-none",
          isDeep
            ? "max-w-[calc(100vw-7.5rem)] sm:max-w-[13.5rem] lg:max-w-[15rem]"
            : "max-w-[12rem] sm:max-w-[18rem]",
          isCurrent
            ? "bg-[#2144FF] text-white shadow-[0_12px_28px_rgba(33,68,255,0.22)]"
            : isDisabled
              ? "cursor-not-allowed text-[#5E6B86]"
              : isDeep
                ? "border border-transparent text-[#344361] hover:border-[#CAD7FF] hover:bg-[#F6F1FF] hover:text-[#2144FF] focus-visible:text-[#2144FF] motion-safe:hover:-translate-y-0.5"
                : "text-[#344361] hover:bg-white hover:text-[#2144FF] hover:shadow-[0_8px_18px_rgba(33,68,255,0.08)] focus-visible:text-[#2144FF] motion-safe:hover:-translate-y-0.5",
        );

        return (
          <li
            key={`${item.href}-${label}-${index}`}
            className={cn("flex min-w-0 items-center", isDeep ? "gap-0.5" : "shrink-0 gap-1.5")}
          >
            {isCurrent ? (
              <span aria-current="page" className={contentClassName} title={label}>
                <span className="truncate">{label}</span>
              </span>
            ) : isDisabled ? (
              <span aria-disabled="true" className={contentClassName} title={label}>
                <span className="truncate">{label}</span>
              </span>
            ) : (
              <Link className={contentClassName} href={item.href} title={label}>
                <span className="truncate">{label}</span>
              </Link>
            )}
            {index < items.length - 1 ? <BreadcrumbSeparator variant={variant} /> : null}
          </li>
        );
      })}
    </ol>
  );

  return (
    <section className="py-4 sm:py-5 lg:py-6" data-kaaj-block="breadcrumb" data-variant={variant}>
      <div className="kaaj-container">
        <nav
          aria-label="Breadcrumb"
          className={cn(
            "relative isolate max-w-full border border-[#D9E1FF] shadow-[0_14px_34px_rgba(21,25,39,0.05)]",
            isDeep
              ? "w-full rounded-[1.65rem] bg-[#FAFBFF] p-2 sm:p-2.5"
              : "w-fit overflow-x-auto rounded-full bg-[#FAFBFF] p-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          )}
        >
          {isDeep ? (
            <div className="flex min-w-0 flex-col gap-3 rounded-[1.25rem] border border-white/85 bg-white px-3 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] sm:flex-row sm:items-center sm:gap-4 sm:px-4">
              {breadcrumbItems}
            </div>
          ) : (
            breadcrumbItems
          )}
        </nav>
      </div>
    </section>
  );
};

BreadcrumbBlock.displayName = "Block::BreadcrumbBlock";
